import React, { useState } from "react";
import { CTASection } from "./ui/cta-section";
import { Button } from "./ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "./ui/input";
import { ErrorMessage, FormField } from "./ui/form-field";
import { useMutation } from "@tanstack/react-query";

import {
  type LeadMagnetForm,
  LeadMagnetFormSchema,
  submitLeadAction,
} from "~/routes/$username/_profile";

export interface LeadMagnetInfo {
  leadMagnetTitle: string;
  leadMagnetDescription?: string | null;
  leadMagnetCtaText?: string | null;
  coachId: string;
}

export const CoachLeadMagnet = ({
  leadMagnetTitle,
  leadMagnetDescription,
  leadMagnetCtaText,
  coachId,
}: LeadMagnetInfo) => {
  const [successResponse, setSuccessResponse] = useState<{
    url: string;
  } | null>(null);

  const { register, handleSubmit, formState, setError } =
    useForm<LeadMagnetForm>({
      resolver: zodResolver(LeadMagnetFormSchema),
      defaultValues: {
        firstName: "",
        email: "",
        coachId,
      },
    });

  const { errors, isSubmitting } = formState;

  const submitMutation = useMutation({
    mutationFn: async (data: LeadMagnetForm) => {
      return await submitLeadAction({ data });
    },
    onSuccess: (response: { url: string }) => {
      setSuccessResponse(response);
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : "Something went wrong";
      setError("root", { message: errorMessage });
    },
  });

  const onSubmit = (data: LeadMagnetForm) => {
    submitMutation.mutate(data);
  };

  return (
    <CTASection
      title={leadMagnetTitle}
      description={leadMagnetDescription ?? undefined}
    >
      {successResponse ? (
        <div className="mt-4 flex flex-col items-center gap-4">
          <p>Thank you!</p>
          <a
            href={successResponse.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="fancy" size="lg">
              {leadMagnetCtaText ?? "Get Instant Access"}
            </Button>
          </a>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
          <input type="hidden" {...register("coachId")} value={coachId} />

          <FormField label="First Name" error={errors.firstName?.message}>
            <Input {...register("firstName")} placeholder="First Name..." />
          </FormField>

          <FormField label="Email" error={errors.email?.message}>
            <Input {...register("email")} placeholder="Email..." type="email" />
          </FormField>

          <Button
            variant="fancy"
            size="lg"
            type="submit"
            disabled={isSubmitting}
            className="mt-2"
          >
            {leadMagnetCtaText ?? "Get Instant Access"}
          </Button>

          {errors.root && (
            <div className="mt-2">
              <ErrorMessage error={errors.root.message} />
            </div>
          )}
        </form>
      )}
    </CTASection>
  );
};
